import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import type { ClaimRequestLookup } from "@rago/web-shared";
import { api } from "../../lib/apiClient";
import { isSearchFlag } from "../../lib/appMode";
import { ApiProblemError, auth } from "../../lib/authManager";
import { useAuth } from "../../lib/useAuth";
import { LinkResultCard, PopupFrame } from "../../components/approval-card";
import { ClaimConsentCard } from "../../components/claim-consent";
import { CheckIcon, ClockIcon, XIcon } from "../../components/icons";
import { LoadingState } from "../../components/page-state";
import { ErrorNote } from "../../components/ui";

/**
 * Consent page for the server-hosted /setup wizard: the window that page
 * opens (`?popup=1`), or a plain tab when the link was opened by hand — on
 * any device, even a phone. The approval windows' look (Claude Design 3c,
 * components/approval-card) in the bare frame (no app bar, routes/_authed).
 * The path param survives the sign-in round trip. After approval the
 * server's own cloud poll picks the claim up; nothing travels back through
 * this window, and Cancel only ever closes it — the request is left to
 * expire, so the wizard can reopen the window.
 */
export const Route = createFileRoute("/_authed/claim_/approve/$code")({
  // The router JSON-parses search values: `?popup=1` arrives as the number 1.
  validateSearch: z.object({
    popup: z.union([z.string(), z.number(), z.boolean()]).optional(),
  }),
  component: ApproveClaim,
});

/** The name /setup gives the window it opens: wizards in the field, built
 * before `popup=1`, are still recognised by it. */
const WIZARD_WINDOW = "rago-claim";
const CLOSE_DELAY_MS = 1500;
/** How long a window gets to close before this page concludes it is a tab. */
const CLOSE_GRACE_MS = 150;

function problemCode(err: unknown): string | null {
  return err instanceof ApiProblemError ? (err.problem.code as string) : null;
}

function problemStatus(err: unknown): number | null {
  return err instanceof ApiProblemError ? err.problem.status : null;
}

/** The request is gone: expired, already approved, or never there. */
function isGone(err: unknown): boolean {
  const code = problemCode(err);
  return code === "claim_request_expired" || code === "not_found";
}

function describeError(err: unknown): string {
  if (!(err instanceof ApiProblemError)) {
    return "Can't reach Rago right now. Check your connection and try again.";
  }
  const p = err.problem;
  if (p.status === 429 || (p.code as string) === "rate_limited") {
    return "Too many attempts. Wait a few minutes and try again.";
  }
  return p.detail ?? p.title;
}

function ApproveClaim() {
  const { code } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const state = useAuth();
  const userId = state.status === "signedIn" ? state.user.id : null;
  const [done, setDone] = useState(false);
  const [ownedByOther, setOwnedByOther] = useState(false);
  const [gone, setGone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  // Opened by the wizard as its window: read once, the window's name and
  // opener do not change.
  const [popupMode] = useState(
    () =>
      isSearchFlag(search.popup) || window.name === WIZARD_WINDOW || window.opener !== null,
  );

  const upper = code.toUpperCase();
  // Keyed by the account: the verdict ("owned by another account") is the
  // signed-in user's, and "Use another account" must ask again.
  const lookup = useQuery({
    queryKey: ["claim-approve", upper, userId],
    queryFn: () =>
      api<ClaimRequestLookup>(`/v1/claim/requests/lookup?code=${encodeURIComponent(upper)}`),
    enabled: userId !== null && !done,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Popup nicety: the wizard advances by itself once the server's poll
  // lands, so close this window after the user has seen the confirmation.
  useEffect(() => {
    if (!(done && popupMode)) return;
    const t = window.setTimeout(() => window.close(), CLOSE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [done, popupMode]);

  async function approve() {
    if (!lookup.data) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/claim/requests/${lookup.data.id}/approve`, { method: "POST", body: {} });
      setDone(true);
    } catch (err) {
      // Gone or taken while the card was up: that state's card, not a note
      // on a card that can no longer be approved.
      if (isGone(err)) setGone(true);
      else if (problemStatus(err) === 403) setOwnedByOther(true);
      else setError(err);
    } finally {
      setBusy(false);
    }
  }

  /** Close the window; the wizard keeps waiting and can reopen it. close()
   * is a no-op outside a script-opened window: a tab still here a moment
   * later goes home instead. */
  function leave() {
    window.close();
    window.setTimeout(() => void navigate({ to: "/" }), CLOSE_GRACE_MS);
  }

  const leaveButton = (
    <button
      type="button"
      className="rago-btn rago-btn-quiet rago-btn-lg min-w-0 flex-1"
      onClick={leave}
    >
      {popupMode ? "Close window" : "Go to Rago"}
    </button>
  );

  function card() {
    // Approved first: from then on the lookup answers "no such request".
    if (done) {
      return (
        <LinkResultCard tone="ok" icon={<CheckIcon size={26} bold />} title="Server claimed">
          {popupMode
            ? "Go back to the setup page. It finishes by itself, and this window closes."
            : "Go back to your server's setup page. It finishes by itself."}
        </LinkResultCard>
      );
    }
    if (ownedByOther || lookup.data?.alreadyOwnedByOther) {
      return (
        <LinkResultCard
          tone="muted"
          icon={<XIcon size={24} />}
          title="This server belongs to another account"
          actions={
            <>
              {/* The account chip's own way out: sign-in, and `next` brings
                  the window back to this same request. */}
              <button
                type="button"
                className="rago-btn rago-btn-lg min-w-0 flex-1"
                onClick={() => void auth.signOut()}
              >
                Use another account
              </button>
              {leaveButton}
            </>
          }
        >
          Its owner has to remove it from their account before it can be claimed again.
        </LinkResultCard>
      );
    }
    if (gone || (lookup.isError && isGone(lookup.error))) {
      return (
        <LinkResultCard
          tone="muted"
          icon={<ClockIcon size={24} />}
          title="This claim request has expired"
          actions={leaveButton}
        >
          Start the claim again from your server's setup page.
        </LinkResultCard>
      );
    }
    // The broker unreachable or rate-limiting: say so, offer another try.
    if (lookup.isError) {
      return (
        <LinkResultCard
          tone="muted"
          icon={<XIcon size={24} />}
          title="Couldn't load this request"
          actions={
            <>
              <button
                type="button"
                className="rago-btn rago-btn-lg min-w-0 flex-1"
                onClick={() => void lookup.refetch()}
              >
                Try again
              </button>
              {leaveButton}
            </>
          }
        >
          {describeError(lookup.error)}
        </LinkResultCard>
      );
    }
    if (!lookup.data) return <LoadingState minH="min-h-[240px]" label="Loading the request" />;
    return (
      <ClaimConsentCard
        request={lookup.data}
        code={upper}
        busy={busy}
        error={error ? <ErrorNote error={error}>{describeError(error)}</ErrorNote> : null}
        onApprove={() => void approve()}
        onCancel={leave}
      />
    );
  }

  return <PopupFrame>{card()}</PopupFrame>;
}
